/**
 * Histogram: satu sebaran, atau beberapa yang dibandingkan.
 *
 * Satu komponen dipakai dua tempat — panel kecil di samping crossplot dan layar
 * histogram yang penuh — sebab keduanya harus menggambar hal yang sama dengan
 * cara yang sama. Sebuah sebaran yang terlihat menjulur ke kanan pada panel
 * kecil dan setangkup pada layar penuh adalah dua gambar yang tidak bisa
 * dipercaya keduanya.
 *
 * Beberapa kelompok digambar bertumpang, bukan bertumpuk, dan itu keputusan
 * bacaan: tumpukan menjawab "berapa seluruhnya", tumpangan menjawab "apakah
 * keduanya sebaran yang sama" — dan yang kedua itulah alasan orang memecah
 * histogram menurut sumur atau zona sejak awal. Tumpukan tetap ditawarkan,
 * sebab pertanyaan pertama juga ada.
 */

import { useMemo } from "react";

import { coordinateStep, coordinateTicks, logTicks, tickDecimals } from "../lib/ticks";
import { bars } from "../lib/xplot";
import type { FieldHistogram } from "../types";
import { Empty } from "./ui";

export type HistogramMode = "count" | "percent" | "cumulative";
export type HistogramLayout = "overlay" | "stack";

const PAD = 3;
const SPAN = 100 - PAD * 2;

/** Kedudukan sebuah nilai pada sumbu mendatar, 0..1. */
function place(value: number, lo: number, hi: number, log: boolean): number {
  const mapped = log ? Math.log10(value) : value;
  const low = log ? Math.log10(lo) : lo;
  const high = log ? Math.log10(hi) : hi;
  return (mapped - low) / (high - low || 1);
}

export function HistogramChart({
  data,
  mode = "count",
  layout = "overlay",
  height,
  showCumulative = false,
  showAxisLabel = false,
}: {
  data: FieldHistogram;
  mode?: HistogramMode;
  layout?: HistogramLayout;
  /** Tinggi tetap dalam piksel; tanpa ini ia mengisi ruang yang ada. */
  height?: number;
  /** Garis kumulatif di atas batangnya, pada sumbu kanan 0–100%. */
  showCumulative?: boolean;
  /** Nama kurva di bawah sumbunya; dimatikan di panel sempit yang sudah
   *  menyebutkan namanya di kepala. */
  showAxisLabel?: boolean;
}) {
  const { edges, groups, log } = data;

  const geometry = useMemo(() => {
    if (edges.length < 2 || !groups.length) return null;
    const lo = edges[0];
    const hi = edges[edges.length - 1];
    // Puncak bersama untuk seluruh kelompok. Menormalkan tiap kelompok terhadap
    // puncaknya sendiri membuat dua sebaran yang berbeda cacahnya sepuluh kali
    // lipat terlihat sama besar — dan perbandingan itulah gunanya memecahnya.
    const series = groups.map((group) =>
      mode === "percent" ? group.percent : mode === "cumulative" ? group.cumulative : group.counts,
    );
    const peak =
      mode === "cumulative"
        ? 100
        : layout === "stack"
          ? Math.max(
              ...series[0].map((_, index) => series.reduce((sum, row) => sum + (row[index] ?? 0), 0)),
              0,
            )
          : Math.max(...series.flat(), 0);

    const stackBase = series[0]?.map(() => 0) ?? [];
    const drawn = groups.map((group, groupIndex) => {
      const rows = bars(edges, series[groupIndex], peak);
      return {
        group,
        bars: rows.map((bar, index) => {
          const base = layout === "stack" ? stackBase[index] / (peak || 1) : 0;
          if (layout === "stack") stackBase[index] += series[groupIndex][index] ?? 0;
          return {
            x0: PAD + place(bar.from, lo, hi, log) * SPAN,
            x1: PAD + place(bar.to, lo, hi, log) * SPAN,
            base,
            height: bar.height,
            value: bar.value,
            from: bar.from,
            to: bar.to,
          };
        }),
      };
    });

    const step = coordinateStep(hi - lo, 5);
    const ticks = (log ? logTicks(lo, hi) : coordinateTicks(lo, hi, step)).map((value) => ({
      value,
      t: place(value, lo, hi, log),
      decimals: log ? tickDecimals(Math.abs(value) || 1) : tickDecimals(step),
    }));
    return { drawn, peak, ticks, lo, hi };
  }, [edges, groups, log, mode, layout]);

  if (!geometry) return <Empty>No samples in this scope.</Empty>;

  const cumulative =
    showCumulative && mode !== "cumulative"
      ? groups.map((group) => ({
          group,
          d: group.cumulative
            .map((value, index) => {
              const x = PAD + place(edges[index + 1] ?? geometry.hi, geometry.lo, geometry.hi, log) * SPAN;
              const y = PAD + (1 - value / 100) * SPAN;
              return `${index === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)}`;
            })
            .join(" "),
        }))
      : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: height ? "none" : 1, minHeight: 0 }}>
      <div
        style={{
          position: "relative",
          flex: height ? "none" : 1,
          height,
          minHeight: 90,
          background: "var(--canvas-deep)",
          border: "1px solid var(--line-strong)",
          borderRadius: "var(--radius-panel)",
          overflow: "hidden",
          boxShadow: "var(--shadow-panel)",
        }}
      >
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          aria-label={`Histogram ${data.curve}`}
        >
          {geometry.ticks.map((tick) => (
            <line
              key={`g${tick.value}`}
              x1={PAD + tick.t * SPAN}
              x2={PAD + tick.t * SPAN}
              y1={0}
              y2={100}
              stroke="var(--grid-line-strong)"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {geometry.drawn.map(({ group, bars: rows }) =>
            rows.map((bar, index) =>
              bar.height > 0 ? (
                <rect
                  key={`${group.key}-${index}`}
                  x={bar.x0}
                  width={Math.max(0.2, bar.x1 - bar.x0)}
                  y={PAD + (1 - bar.base - bar.height) * SPAN}
                  height={Math.max(0.3, bar.height * SPAN)}
                  fill={group.color}
                  // Bertumpang berarti yang di belakang harus tetap terbaca.
                  fillOpacity={geometry.drawn.length > 1 ? 0.55 : 0.9}
                  stroke={group.color}
                  strokeWidth={0.4}
                  vectorEffect="non-scaling-stroke"
                >
                  <title>
                    {group.label} · {bar.from.toFixed(3)}–{bar.to.toFixed(3)} ·{" "}
                    {mode === "count" ? bar.value : `${bar.value.toFixed(1)}%`}
                  </title>
                </rect>
              ) : null,
            ),
          )}
          {cumulative.map(({ group, d }) => (
            <path
              key={`c${group.key}`}
              d={d}
              fill="none"
              stroke={group.color}
              strokeWidth={1.4}
              strokeDasharray="4 2"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>
      </div>
      <div style={{ position: "relative", height: 12, flex: "none" }}>
        {geometry.ticks.map((tick) => (
          <span
            key={tick.value}
            style={{
              position: "absolute",
              left: `${PAD + tick.t * SPAN}%`,
              transform: "translateX(-50%)",
              font: "400 9px var(--font-mono)",
              color: "var(--ink-5)",
              whiteSpace: "nowrap",
            }}
          >
            {tick.value.toFixed(tick.decimals)}
          </span>
        ))}
      </div>
      {showAxisLabel && (
        <span
          style={{
            font: "500 10px var(--font-mono)",
            color: "var(--ink-4)",
            textAlign: "center",
            flex: "none",
          }}
        >
          {data.curve}
          {log ? " · log" : ""}
        </span>
      )}
    </div>
  );
}

/** Legenda kelompok: warna, nama, dan berapa sampel yang dibawanya. */
export function HistogramLegend({ data }: { data: FieldHistogram }) {
  if (data.groups.length < 2) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
      {data.groups.map((group) => (
        <span
          key={group.key}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            font: "400 9.5px var(--font-mono)",
            color: "var(--ink-5)",
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: 2, background: group.color }} />
          {group.label}
          <span style={{ color: "var(--ink-7)" }}>{group.stats?.n.toLocaleString("en-US") ?? 0}</span>
        </span>
      ))}
    </div>
  );
}
